import { useEffect, useState, useRef, useCallback } from "react";
import { VendorFormProvider, useVendorForm } from "./VendorFormContext";
import { getAllSalesVendors, searchVendors, updateSalesContact } from "../../../api/sales";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSelectedVendor } from "../../../features/vendorSlice";
import VendorHeader from "./components/VendorHeader";
import VendorTable from "./components/VendorTable";
import SalesContactsModal from "../Contacts/components/SalesContactsModal";
import axiosInstance from "../../../api/axiosInstance";
import { getUserInfoDetails } from "../../../utlis";

const VendorsInner = () => {
  const [vendors, setVendors] = useState([]);
  const [backendResults, setBackendResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { startCreateNewVendor } = useVendorForm();
  const searchTimerRef = useRef(null);

  // Filter state
  const [isFilterPopupOpen, setFilterPopupOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);

  // Edit contact modal state
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const getData = async () => {
    setLoading(true);
    try {
      const response = await getAllSalesVendors();
      const vendorData = response.data?.response?.data || [];
      setVendors(Array.isArray(vendorData) ? vendorData : []);
    } catch (error) {
      const statusCode = error.response?.status;

      if (error.message === 'Network Error' || !error.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 403) {
        toast.error('Access denied: You do not have permission to view vendors');
      } else if (statusCode === 500) {
        toast.error('Server error. Please try again later');
      } else {
        toast.error('Failed to load vendors. Please try again');
      }
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // Handle opening edit contact modal — fetches contact on demand
  const handleEditContact = async (contactId) => {
    try {
      const { companyId, clientId } = getUserInfoDetails();
      const res = await axiosInstance.get('/contacts', {
        params: { company: companyId, client: clientId },
      });
      const allContacts = res.data.data.records || [];
      const fullContactData = allContacts.find((c) => c.contact_id === contactId);

      if (!fullContactData) {
        toast.error('Contact details not found.');
        return;
      }

      const createdDate = new Date(fullContactData.created);
      const updatedDate = new Date(fullContactData.updated);

      const contactData = {
        firstName: fullContactData.first_name,
        middleName: fullContactData.middle_name,
        lastName: fullContactData.last_name,
        preferredName: fullContactData.preferred_name,
        email: fullContactData.email,
        jobTitle: fullContactData.job_title,
        mobileNum1: fullContactData?.phone?.mobile,
        mobileNum2: fullContactData?.phone?.phone,
        fax: fullContactData?.phone?.Fax || '',
        contactType: fullContactData.contact_type || '',
        address1: fullContactData.address?.address_line1,
        address2: fullContactData.address?.address_line2,
        city: fullContactData.address?.city,
        state: fullContactData.address?.state,
        country: fullContactData.address?.country,
        zipCode: fullContactData.address?.zip,
        notes: fullContactData.note || '',
        vendorStatus: fullContactData.status || 'Active',
        contact_id: fullContactData.contact_id,
        createdDate: createdDate.toISOString().split('T')[0] || '',
        createdTime: createdDate.toISOString().split('T')[1]?.slice(0, 5) || '',
        updateDate: updatedDate.toISOString().split('T')[0] || '',
        updateTime: updatedDate.toISOString().split('T')[1]?.slice(0, 5) || '',
        created_by: fullContactData.created_by,
        updated_by: fullContactData.updated_by,
      };

      setEditingContact(contactData);
      setIsEditContactModalOpen(true);
    } catch (err) {
      toast.error('Failed to load contact details. Please try again.');
    }
  };

  // Handle edit contact form submission
  const handleEditContactSubmit = async (formData) => {
    const payload = {
      divison: 'div',
      type: formData.contactType,
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      email: formData.email,
      preferredName: formData.preferredName,
      jobTitle: formData.jobTitle,
      phone: {
        mobile: formData.mobileNum1,
        phone: formData.mobileNum2,
        Fax: formData.fax,
      },
      address: {
        address_line2: formData.address2,
        address_line1: formData.address1,
        city: formData.city,
        state: formData.state,
        zip: formData.zipCode,
        country: formData.country,
      },
      status: formData.vendorStatus || 'Active',
      contact_id: formData.contact_id,
    };

    try {
      await updateSalesContact(payload);
      setIsEditContactModalOpen(false);
      setEditingContact(null);
      toast.success('Contact Updated Successfully');
    } catch (error) {
      toast.error('Failed to update contact. Please try again');
    }
  };

  const closeEditContactModal = () => {
    setIsEditContactModalOpen(false);
    setEditingContact(null);
  };

  // Backend search fallback — debounced to avoid excessive API calls
  const performBackendSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setBackendResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await searchVendors(term);
      const records = response.data?.data?.records || [];
      setBackendResults(records);
    } catch (error) {
      setBackendResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change — local first, then backend fallback
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear previous timer
    clearTimeout(searchTimerRef.current);

    if (!value.trim()) {
      setBackendResults([]);
      return;
    }

    // Debounce backend search by 500ms
    searchTimerRef.current = setTimeout(() => {
      performBackendSearch(value.trim());
    }, 500);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Filter handlers
  const handleFilterClick = () => {
    setFilterPopupOpen(true);
  };

  const handleCloseFilterPopup = () => {
    setFilterPopupOpen(false);
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters || []);
    setFilterPopupOpen(false);
  };

  // Filter search results for FilterPopup
  const getSearchResults = (searchTerm, selectedCategory) => {
    if (!searchTerm || !selectedCategory || !vendors) return [];

    return vendors
      .filter((vendor) => {
        let valueToSearch = '';

        switch (selectedCategory) {
          case 'Vendor Name':
            valueToSearch = vendor.name || '';
            break;
          case 'Vendor Type':
            valueToSearch = vendor.type || '';
            break;
          case 'Phone Number':
            valueToSearch = vendor.phone || '';
            break;
          case 'Email':
            valueToSearch = vendor.email || '';
            break;
          case 'Contact':
            const contact = vendor.contacts?.[0];
            valueToSearch = contact
              ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
              : '';
            break;
          case 'Status':
            valueToSearch = vendor.status || '';
            break;
          default:
            valueToSearch = '';
        }

        return valueToSearch.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .map((vendor) => {
        switch (selectedCategory) {
          case 'Vendor Name':
            return vendor.name;
          case 'Vendor Type':
            return vendor.type;
          case 'Phone Number':
            return vendor.phone;
          case 'Email':
            return vendor.email;
          case 'Contact':
            const contact = vendor.contacts?.[0];
            return contact
              ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
              : null;
          case 'Status':
            return vendor.status;
          default:
            return null;
        }
      })
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  const filterCategories = [
    'Vendor Name',
    'Vendor Type',
    'Phone Number',
    'Email',
    'Contact',
    'Status',
  ];

  // Handle add new vendor
  const handleAddVendor = () => {
    dispatch(setSelectedVendor(null));
    startCreateNewVendor();
    navigate('summary/account-info');
  };

  // Handle vendor click/edit
  const handleEditVendor = (vendor) => {
    if (vendor && vendor.vendor_id) {
      dispatch(setSelectedVendor(vendor));
      navigate('summary/account-info');
    } else {
      toast.error('Vendor ID not found');
    }
  };

  // Filter vendors based on search term and applied filters
  const localFiltered = vendors.filter((vendor) => {
    const searchLower = searchTerm.toLowerCase();
    const contactName = vendor.contacts?.[0]
      ? `${vendor.contacts[0].firstName || ''} ${vendor.contacts[0].lastName || ''}`.trim()
      : '';

    // Apply search filter
    const matchesSearch = !searchTerm || (
      vendor.name?.toLowerCase().includes(searchLower) ||
      vendor.type?.toLowerCase().includes(searchLower) ||
      vendor.email?.toLowerCase().includes(searchLower) ||
      vendor.phone?.toLowerCase().includes(searchLower) ||
      vendor.status?.toLowerCase().includes(searchLower) ||
      contactName.toLowerCase().includes(searchLower)
    );

    // Apply category filters
    const matchesFilters = !appliedFilters.length || appliedFilters.some(({ category, items }) => {
      if (!items || items.length === 0) return false;

      switch (category) {
        case 'Vendor Name':
          return items.some((name) => name.toLowerCase() === (vendor.name || '').toLowerCase());
        case 'Vendor Type':
          return items.some((type) => type.toLowerCase() === (vendor.type || '').toLowerCase());
        case 'Phone Number':
          return items.some((phone) => phone === vendor.phone);
        case 'Email':
          return items.some((email) => email.toLowerCase() === (vendor.email || '').toLowerCase());
        case 'Contact':
          return items.some((name) => name.toLowerCase() === contactName.toLowerCase());
        case 'Status':
          return items.some((status) => status.toLowerCase() === (vendor.status || '').toLowerCase());
        default:
          return false;
      }
    });

    return matchesSearch && matchesFilters;
  });

  // Use local results if available, otherwise fall back to backend search results
  // Merge and deduplicate by vendor_id
  const filteredVendors = (() => {
    if (localFiltered.length > 0 || !searchTerm.trim()) return localFiltered;
    if (backendResults.length === 0) return localFiltered;
    // Backend results found — merge with local, deduplicate by vendor_id
    const localIds = new Set(localFiltered.map((v) => v.vendor_id));
    const merged = [...localFiltered, ...backendResults.filter((v) => !localIds.has(v.vendor_id))];
    return merged;
  })();

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <VendorHeader
          onReload={getData}
          onAddVendor={handleAddVendor}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Backend search indicator */}
        {searchTerm.trim() && localFiltered.length === 0 && backendResults.length > 0 && (
          <div className="px-4 py-2 bg-blue-50 text-blue-700 text-sm border-b border-blue-100">
            Showing results from server search for "{searchTerm}"
          </div>
        )}
        {searchTerm.trim() && isSearching && localFiltered.length === 0 && (
          <div className="px-4 py-2 bg-gray-50 text-gray-500 text-sm border-b border-gray-100">
            Searching server...
          </div>
        )}

        {/* Table with loading overlay */}
        <div className="relative">
          <VendorTable
            data={filteredVendors}
            isLoading={isLoading}
            onEditVendor={handleEditVendor}
            onEditContact={handleEditContact}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            isFilterPopupOpen={isFilterPopupOpen}
            onFilterClick={handleFilterClick}
            onCloseFilterPopup={handleCloseFilterPopup}
            onApplyFilters={handleApplyFilters}
            getSearchResults={getSearchResults}
            filterCategories={filterCategories}
            tableData={vendors}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Edit Contact Modal */}
      <SalesContactsModal
        isOpen={isEditContactModalOpen}
        onClose={closeEditContactModal}
        onSubmit={handleEditContactSubmit}
        isEditMode={true}
        defaultValues={editingContact}
      />
    </div>
  );
};

const Vendors = () => (
  <VendorFormProvider>
    <VendorsInner />
  </VendorFormProvider>
);

export default Vendors;
